import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { secondsToHours } from './chartUtils';
import type { ProjectBreakdownResponse } from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import type { DoneTotals } from '@its4plan/ordering';
import useTranslate from '~/common/utils/translate';

interface Props {
  breakdown: ProjectBreakdownResponse;
  doneTotals: DoneTotals | undefined;
}

export const TimeComparisonChart: React.FC<Props> = ({ breakdown, doneTotals }) => {
  const t = useTranslate();
  const chartData = useMemo(() => {
    const adminCalc = breakdown.calculated_project_time_seconds ?? 0;
    const jobSum = breakdown.project_time_seconds;
    const totalDone = doneTotals?.totalDoneAmount ?? 0;
    const avgBooked = totalDone > 0 ? breakdown.total_duration_seconds / totalDone : null;
    return [{
      name: t('projectRecords.chart.comparison'),
      adminCalculated: secondsToHours(adminCalc),
      jobSum: secondsToHours(jobSum),
      avgBooked: avgBooked != null ? secondsToHours(avgBooked) : 0,
    }];
  }, [breakdown, doneTotals, t]);

  const hasAvgBooked = (doneTotals?.totalDoneAmount ?? 0) > 0;

  return (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
      <Text fontSize="sm" fontWeight="600" mb={2}>
        {t('projectRecords.chart.timeComparison')}
      </Text>
      <Box h="200px">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} unit="h" />
            <Tooltip />
            <Legend />
            <Bar dataKey="adminCalculated" name={t('projectRecords.chart.adminCalcTime')} fill="#805ad5" />
            <Bar dataKey="jobSum" name={t('projectRecords.chart.jobSumTime')} fill="#3182ce" />
            {hasAvgBooked && (
              <Bar dataKey="avgBooked" name={t('projectRecords.chart.avgBookedTime')} fill="#e53e3e" />
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {!hasAvgBooked && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {t('projectRecords.chart.noDoneUnits')}
        </Text>
      )}
    </Box>
  );
};
